import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Settings as SettingsIcon, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = ({ user }) => {
  const [settings, setSettings] = useState({
    company_name: 'Underplay Music',
    vat_number: '',
    company_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    email: '',
    phone: '',
    bank_name: '',
    account_number: '',
    sort_code: '',
    iban: '',
    swift_code: '',
    payment_notes: '',
    default_currency: 'GBP',
  });
  
  const CURRENCIES = [
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
    { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
    { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
    { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
    { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  ];
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      
      // No settings found - use defaults
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="artist-details-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Artist Details</h1>
          <p className="text-muted-foreground text-base mt-2">
            Configure your company and bank details for invoices
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
          data-testid="save-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Company Information */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Company Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                data-testid="company-name-input"
                placeholder="Your company name"
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vat_number">VAT/Tax Number</Label>
              <Input
                id="vat_number"
                data-testid="vat-number-input"
                placeholder="Your VAT number (e.g., GB123456789)"
                value={settings.vat_number}
                onChange={(e) => setSettings({ ...settings, vat_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_number">Company Number</Label>
              <Input
                id="company_number"
                data-testid="company-number-input"
                placeholder="Your company registration number"
                value={settings.company_number}
                onChange={(e) => setSettings({ ...settings, company_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Company Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="company-email-input"
                placeholder="Your company email address"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Company Phone</Label>
              <Input
                id="phone"
                data-testid="company-phone-input"
                placeholder="Your company phone number"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_currency">Default Currency</Label>
              <select
                id="default_currency"
                data-testid="default-currency-select"
                value={settings.default_currency}
                onChange={(e) => setSettings({ ...settings, default_currency: e.target.value })}
                className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.symbol} {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">All financial reports will consolidate to this currency</p>
            </div>
          </div>
        </Card>

        {/* Address */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Company Address</h2>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input
                id="address_line1"
                data-testid="address-line1-input"
                placeholder="Your street address"
                value={settings.address_line1}
                onChange={(e) => setSettings({ ...settings, address_line1: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
              <Input
                id="address_line2"
                data-testid="address-line2-input"
                placeholder="Suite, floor, building (optional)"
                value={settings.address_line2}
                onChange={(e) => setSettings({ ...settings, address_line2: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  data-testid="city-input"
                  placeholder="Your city"
                  value={settings.city}
                  onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode</Label>
                <Input
                  id="postcode"
                  data-testid="postcode-input"
                  placeholder="Your postcode"
                  value={settings.postcode}
                  onChange={(e) => setSettings({ ...settings, postcode: e.target.value })}
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  data-testid="country-input"
                  value={settings.country}
                  onChange={(e) => setSettings({ ...settings, country: e.target.value })}
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Payment Details */}
        <Card className="bg-card border-border/50 p-6">
          <h2 className="font-manrope font-bold text-2xl text-foreground mb-6">Payment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bank_name">Bank Name</Label>
              <Input
                id="bank_name"
                data-testid="bank-name-input"
                placeholder="Barclays Bank"
                value={settings.bank_name}
                onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sort_code">Sort Code</Label>
              <Input
                id="sort_code"
                data-testid="sort-code-input"
                placeholder="12-34-56"
                value={settings.sort_code}
                onChange={(e) => setSettings({ ...settings, sort_code: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="account_number">Account Number</Label>
              <Input
                id="account_number"
                data-testid="account-number-input"
                placeholder="12345678"
                value={settings.account_number}
                onChange={(e) => setSettings({ ...settings, account_number: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                data-testid="iban-input"
                placeholder="GB29 NWBK 6016 1331 9268 19"
                value={settings.iban}
                onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="swift_code">SWIFT Code</Label>
              <Input
                id="swift_code"
                data-testid="swift-code-input"
                placeholder="BARCGB22XXX"
                value={settings.swift_code}
                onChange={(e) => setSettings({ ...settings, swift_code: e.target.value })}
                className="bg-input/50 border-border rounded-xl"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="payment_notes">Payment Notes (Optional)</Label>
              <Textarea
                id="payment_notes"
                data-testid="payment-notes-input"
                placeholder="Please reference invoice number when making payment..."
                value={settings.payment_notes}
                onChange={(e) => setSettings({ ...settings, payment_notes: e.target.value })}
                className="bg-input/50 border-border rounded-xl min-h-24"
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Settings;